import { Segment } from "../components/Editor/shapes/line/Line";

export interface VectorProjective {
    x: number;
    y: number;
    w: number; //Homogenious coordinate
}
interface PointProjective {
    x: number;
    y: number;
    w: number; //Homogenious coordinate (1)
}
interface LineProjective {
    //ax + by + c = 0
    a: number;
    b: number;
    c: number;
}
export function normalize3(p: { x: number, y: number, z: number }): { x: number, y: number, z: number } {
    const l = 1 / length3(p);
    return { x: p.x * l, y: p.y * l, z: p.z * l };
}
function pointDifference(p1: Point2d, p2: Point2d): VectorProjective {
    return { x: p1.x - p2.x, y: p1.y - p2.y, w: 0 }
}

function length(a: Point2d | VectorProjective) {
    return Math.sqrt(a.x * a.x + a.y * a.y);
}
function length3(a: { x: number, y: number, z: number }) {
    return Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
}
// Vectoralgebra functions
// Perpendicular 2D line
export function perpendicular(vector: VectorProjective) {
    return {
        x: vector.y,
        y: -vector.x,
        w: vector.w
    };
}
/**
 * To determine angle between two lines
 * @returns angle in rasians range is from -PI to PI
 */
export function angleOfTwoLinesFromThreePoints(center: Point2d, firstPoint: Point2d, secondPoint: Point2d): number {
    const lineOne = pointDifference(firstPoint, center);
    const lineTwo = pointDifference(secondPoint, center);
    const l = dot(pointDifference(firstPoint, center), pointDifference(secondPoint, center)) / length(lineOne) / length(lineTwo);
    const sign = dot(lineOne, { x: lineTwo.y, y: -lineTwo.x, w: lineTwo.w });

    return Math.acos(l) * Math.sign(sign);
}

//Subtract line from line
export function subtract(vector1: VectorProjective, vector2: VectorProjective) {
    return {
        x: vector1.x - vector2.x,
        y: vector1.y - vector2.y,
        w: vector1.w - vector2.w
    };
}

export function lineFromPoints(p1: PointProjective, p2: PointProjective, t: number): LineProjective {
    const tOneMinus = 1 - t;
    const obje = {
        a: p1.x * t + p2.x * tOneMinus,
        b: p1.y * t + p2.y * tOneMinus,
        c: p1.w * t + p2.w * tOneMinus,
    }

    return obje;
}

export function lineLineIntersectionFromSegments(s1: Segment, s2: Segment) {
    const line1 = cross({ x: s1[0].x, y: s1[0].y, w: 1 }, { x: s1[1].x, y: s1[1].y, w: 1 })
    const line2 = cross({ x: s2[0].x, y: s2[0].y, w: 1 }, { x: s2[1].x, y: s2[1].y, w: 1 })
    const point = cross(line1, line2);
    return { x: point.x / point.w, y: point.y / point.w }
}
// Vector dot product
export function dot(vector1: VectorProjective, vector2: VectorProjective) {
    return (
        vector1.x * vector2.x +
        vector1.y * vector2.y +
        vector1.w * vector2.w
    );
}

export function segmentSegmentIntersection(segment1p1: Point2d, segment1p2: Point2d, segment2p1: Point2d, segment2p2: Point2d): boolean {
    const normalToSegment1 = { x: segment1p1.y - segment1p2.y, y: -(segment1p1.x - segment1p2.x), w: 0 };
    const firstVectorToOtherStart = subtract({ ...segment2p1, w: 1 }, { ...segment1p1, w: 1 });
    const firstVectorToOtherEnd = subtract({ ...segment2p2, w: 1 }, { ...segment1p1, w: 1 })
    const firstSegmentDotProduct1 = dot(normalToSegment1, firstVectorToOtherStart);
    const firstSegmentDotProduct2 = dot((normalToSegment1), firstVectorToOtherEnd);
    const pointsOnDifferentSideOfSegment1 = (firstSegmentDotProduct1 * firstSegmentDotProduct2 <= 0);

    const normalToSegment2 = { x: segment2p1.y - segment2p2.y, y: -(segment2p1.x - segment2p2.x), w: 0 };
    const secondVectorToOtherStart = subtract({ ...segment1p2, w: 1 }, { ...segment2p1, w: 1 });
    const secondVectorToOtherEnd = subtract({ ...segment1p1, w: 1 }, { ...segment2p1, w: 1 });
    const secondSegmentDotProduct1 = dot((normalToSegment2), secondVectorToOtherStart);
    const secondSegmentDotProduct2 = dot((normalToSegment2), secondVectorToOtherEnd);
    const pointsOnDifferentSideOfSegment2 = (secondSegmentDotProduct1 * secondSegmentDotProduct2 <= 0);

    const onBothSegBothEndsAreOnOtherSideOfSegment = (
        pointsOnDifferentSideOfSegment1 &&
        pointsOnDifferentSideOfSegment2
    );
    return onBothSegBothEndsAreOnOtherSideOfSegment;

}

export function lineRectIntersection(segments: { start: Point2d, end: Point2d }[], r: RectSel): boolean {
    for (const segment of segments) {
        const segmentStatus = segmentRectIntersection(segment, r);
        if (
            segmentStatus === "inTwoPoint" ||
            segmentStatus === "inOnePoint" ||
            segmentStatus === "inByIntersect"
        ) {
            return true
        };
    }
    return false;
}

function isPointInRect(p: Point2d, r: RectSel): boolean {
    // Find Bounds
    const smallx = Math.min(r.x, r.x + r.width);
    const bigx = Math.max(r.x, r.x + r.width);
    const smally = Math.min(r.y, r.y + r.height);
    const bigy = Math.max(r.y, r.y + r.height);
    return (p.x >= smallx && p.x <= bigx && p.y >= smally && p.y <= bigy)
}

export function segmentRectIntersection({ start, end }: { start: Point2d, end: Point2d }, r: RectSel): "inTwoPoint" | "inOnePoint" | "inByIntersect" | "out" {
    const startIn = isPointInRect(start, r)
    const endIn = isPointInRect(end, r)

    if (startIn && endIn) { return "inTwoPoint" }
    else if (startIn !== endIn) { return "inOnePoint" }
    else {
        // CheckSides
        const sidesOfSelectionRect = [
            [{ x: r.x, y: r.y }, { x: r.x + r.width, y: r.y }],
            [{ x: r.x, y: r.y }, { x: r.x, y: r.y + r.height }],
            [{ x: r.x + r.width, y: r.y }, { x: r.x + r.width, y: r.y + r.height }],
            [{ x: r.x, y: r.y + r.height }, { x: r.x + r.width, y: r.y + r.height }],
        ]
        for (const side of sidesOfSelectionRect) {
            if (segmentSegmentIntersection(start, end, side[0], side[1])) return "inByIntersect";
        }
    }
    if (!startIn && !endIn) return "out"
    return "inTwoPoint"
}

// Scalar Multiplication
export function mutiply(vector: VectorProjective, scalar: number) {
    return {
        x: vector.x * scalar,
        y: vector.y * scalar,
        w: vector.w * scalar
    };
}

// Negate vector
export function negate(vector: VectorProjective) {
    return { x: -vector.x, y: -vector.y, w: -vector.w };
}

// Cross Product of vectors
export function cross(vector1: VectorProjective, vector2: VectorProjective) {
    return {
        x: vector1.y * vector2.w - vector1.w * vector2.y,
        y: -(vector1.x * vector2.w - vector1.w * vector2.x),
        w: vector1.x * vector2.y - vector1.y * vector2.x
    }
}

export const distance2d = (point1: Point2d, point2: Point2d) => {
    let dx = point1.x - point2.x;
    let dy = point1.y - point2.y;
    return Math.sqrt(dx * dx + dy * dy);
};

// Find line that is perpendicular to a line and passes through a point
function perpendicularLineOnPoint(point: PointProjective, line: LineProjective) {
    const modifiedLine = { x: line.a, y: line.b, w: 0 };
    return cross(modifiedLine, point);
}

// Find point of Line that is nearest to point
export function pointOnLineNearestToPoint(point: PointProjective, line: LineProjective) {
    const modifiedLine = { x: line.a, y: line.b, w: line.c };
    return cross(perpendicularLineOnPoint(point, line), modifiedLine);
}

// See if point is in the bounding box of a segment
export function inBetweenPoints(lineStart: Point2d, lineEnd: Point2d, p: Point2d, tolerance: number): boolean {
    const x_large = lineStart.x > lineEnd.x ? lineStart.x : lineEnd.x;
    const x_small = lineStart.x > lineEnd.x ? lineEnd.x : lineStart.x;
    const y_large = lineStart.y > lineEnd.y ? lineStart.y : lineEnd.y;
    const y_small = lineStart.y > lineEnd.y ? lineEnd.y : lineStart.y;
    if (p.x > x_small - tolerance && p.x < x_large + tolerance && p.y > y_small - tolerance && p.y < y_large + tolerance) {
        return true;
    }
    return false;
}

// See if point is on the line
export function isOnLine(projectiveLine: LineProjective, projectivePoint: PointProjective): boolean {
    const isNull = projectiveLine.a * projectivePoint.x + projectiveLine.b * projectivePoint.y + projectiveLine.c * projectivePoint.w;

    if (Math.abs(isNull) < 0.001) {
        return true
    } else {
        return false;
    }
}

export function distancePointFromLine(P: Point2d, line: LineProjective): number {
    const Q = { x: 0, y: line.a * 0 + line.c, z: 1 }
    // QP * N / ||N||
    const QP: Point2d = { x: P.x - Q.x, y: P.y - Q.y }
    const N: Point2d = {
        x: (line.a),
        y: (line.b),
    }
    const N_abs = N.x * N.x + N.y * N.y // to have exact length, it should be square root of
    return QP.x * N.x / N_abs + QP.y * N.y / N_abs;
}

export function rotateAroundPoint(sh: Point2d, angleRad: number, point: { x: number, y: number }): Point2d {

    const p = {
        x:
            point.x +
            (sh.x - point.x) * Math.cos(angleRad) -
            (sh.y - point.y) * Math.sin(angleRad),

        y:
            point.y +
            (sh.x - point.x) * Math.sin(angleRad) +
            (sh.y - point.y) * Math.cos(angleRad),
    }
    return (p);
}

export function pointEquals(p1: Point2d, p2: Point2d): boolean {
    const EPS = 0.001
    return Math.abs(p1.x - p2.x) < EPS && Math.abs(p1.y - p2.y) < EPS
}

export function quadraticEquation(a: number, b: number, c: number): number[] {
    const discriminant = b * b - 4 * a * c;
    if (discriminant < 0) return [];
    if (discriminant === 0) {
        const r = -b / (2 * a);
        return [r]
    }
    const sqrtDiscriminant = Math.sqrt(discriminant);
    return [(-b - sqrtDiscriminant) / (2 * a), (-b + sqrtDiscriminant) / (2 * a)]
}

export function lineCircleIntersection(segment: LineSegment2d, { x: cx, y: cy, r }: { x: number, y: number, r: number }): Point2d[] {
    // const crossLine = lineFromPoints({ x: segment.x1, y: segment.y1, w: 1 }, { x: segment.x2, y: segment.y2, w: 1 }, 0.2)
    const crossLine0 = cross({ x: segment.x1, y: segment.y1, w: 1 }, { x: segment.x2, y: segment.y2, w: 1 })
    const line = { a: crossLine0.x, b: crossLine0.y, c: crossLine0.w }
    console.log(line.a, line.b, line.c,);

    let firstCoord;
    const divider = 1 / (line.b * line.b);
    console.log(line.b)
    if (!line.b) {
        const a = 1;
        const b = -2 * cx;
        const c = cx * cx
            + cy * cy
            - r * r
        console.log(`b=0 a:${a}, b:${b}, c:${c} aaa`);
        firstCoord = quadraticEquation(a, b, c)
    } else {
        // Quadratic coefficient (A):
        const a = 1 + (line.a * line.a) * divider;
        // Linear coefficient (B):
        const b = (((line.a * line.c) + (line.a * line.b * cy)) * divider - cx) * 2;
        // Constant term (C):
        const c = (cx * cx)
            + (cy * cy)
            + (line.c * line.c) * divider
            + (2 * line.b * line.c * cy) * divider
            - (r * r);

        firstCoord = quadraticEquation(a, b, c)
    }

    if (firstCoord) {
        return firstCoord.map((root) => {

            return {
                x: root,
                y: (-line.a * root - line.c) / line.b
            }
        })
    }
    return [];
}